/**
 * 36 - Hold the Crossing (10 §2): reach the ford on the river in the belt - the quiet crossing, where the chain
 * of ravines comes out - and hold it, alive and in the zone, for three minutes; leaving the zone stops the
 * clock. Recall is disabled: the Osprey comes on its own the moment the hold is done. The enemy counter-attacks
 * in waves from his camp on the north bank: men who are not on the map until their wave's timed event.
 *
 * The waves are on the HOLD's clock, not the mission's: each is a timed event counted `from: 'zoneEntered'` - the
 * first tick he stands in the ford's zone - so on every map, however long the walk in, the first lands half a
 * minute after he arrives and the rest are spread through the three minutes. `finish` only checks that the
 * mission clock has room for a careful approach, the hold and the Osprey (helpers3aRoute.ts).
 */

import type { ObjectiveRule } from '../../sim/types';
import { checker, clutter, guard, guardAtDoor, guardsAbout, insideRect, ruleOf, structure } from './helpers';
import { checkRuleText, checkTargets, insideMap, inWater, timedRules, waiting } from './helpers3aAsserts';
import { CAREFUL, measureRoute } from './helpers3aRoute';
import { reachAt } from './helpers3aWater';
import type { ArchetypeSpec } from './types';

export const HOLD_SECONDS = 180;
export const wavesFor = (threat: number): number => (threat >= 5 ? 3 : 2);
export const waveSizeFor = (threat: number): number => 2 + (threat >= 4 ? 1 : 0) + (threat >= 7 ? 1 : 0);

/** Seconds after he reaches the ford that the first wave sets out, and the last moment one may: all inside the hold. */
export const FIRST_WAVE = 25;
export const LAST_WAVE = HOLD_SECONDS - 45;
/** The Osprey's 45 s in and 20 s over the X, once the hold is done. */
const OSPREY_SECONDS = 65;

const ORDINAL = ['FIRST', 'SECOND', 'THIRD'] as const;
const tagOf = (wave: number): string => `wave${wave + 1}`;

/** When each wave sets out, in seconds from his arrival at the ford: the first at 25 s, the last at 2:15. */
export function waveSeconds(waves: number): number[] {
  const step = waves > 1 ? (LAST_WAVE - FIRST_WAVE) / (waves - 1) : 0;
  return Array.from({ length: waves }, (_, i) => Math.round(FIRST_WAVE + step * i));
}

export const holdCrossing: ArchetypeSpec = {
  id: 'hold-crossing',
  name: 'HOLD THE CROSSING',
  nativeBiome: 'temperate',
  baseThreat: 5,
  lengthTiles: 160,
  // The approach, three minutes at the ford, and the wait for the Osprey all come out of one clock.
  clockScale: 1.2,
  startAlert: 0,
  garrison: 'regular',
  perimeter: 'wire',
  sealed: false,
  layout: 'compound',
  // Wide, as the radar array's: the ford comes out by the camp's corner.
  size: { w: [54, 62], d: [30, 34] },
  river: true,
  patrolLoop: 'inside',
  breach: { min: 0, max: 0 },

  build(g) {
    const line = g.ctx.plan.lines[g.ctx.plan.lines.length - 1];
    const ford = reachAt(g, line?.quietX ?? g.ctx.sizeX / 2);
    const marker = ford
      ? g.map.add('observationPost', ford.x, Math.round(ford.z * 2) / 2, { target: true })
      : { id: -1, x: 0, z: 0 };
    // Both banks and the water between: he may fight from either, and from the ford itself.
    const zone = ford
      ? {
        x0: Math.max(1, ford.x - 8), z0: ford.north - 4,
        x1: Math.min(g.ctx.sizeX - 1, ford.x + 8), z1: ford.south + 4,
      }
      : { ...g.rect };
    // No picket stands at the ford: it is the end of the quiet way in, and a man posted there would shut it
    // (validate/quiet). The enemy's answer to a ranger at the ford is the waves.

    // The waves form up inside the camp's gates - out of sight of the ford - and go home to them afterwards.
    const size = waveSizeFor(g.threat);
    const gates = g.ctx.plan.gates;
    const rules: ObjectiveRule[] = [];
    const times = waveSeconds(wavesFor(g.threat));
    for (let w = 0; w < wavesFor(g.threat); w++) {
      const gate = gates[(w + 1) % Math.max(1, gates.length)];
      const inward = gate?.side === 'west' ? 5 : gate?.side === 'east' ? -5 : 0;
      const back = gate?.side === 'south' ? 5 : 0;
      const at = { x: (gate?.x ?? g.rect.x0 + 8) + inward, z: (gate?.z ?? g.rect.z1) - back };
      let posted = 0;
      for (let m = 0; m < size; m++) {
        const kind = g.elite || (g.threat >= 6 && m === 0) ? 'elite' : 'rifleman';
        const wakes = { dormantUntil: 'timedEvent', wakeTag: tagOf(w) } as const;
        if (guard(g, at.x + (m % 2) * 2 - 1, at.z - Math.floor(m / 2) * 2 - w, 0, kind, wakes)) posted++;
      }
      if (posted < size) g.map.problems.push(`hold-crossing: nowhere for ${tagOf(w)} to form up`);
      rules.push({
        type: 'timedEvent', atSeconds: times[w] ?? FIRST_WAVE, from: 'zoneEntered', effect: 'searchPartyArrives',
        wakes: tagOf(w),
        orders: 'THE ENEMY COUNTER-ATTACKS IN WAVES ONCE YOU ARE AT THE FORD: THE FIRST HALF A MINUTE AFTER YOU'
          + ' ARRIVE',
        warning: `${ORDINAL[w] ?? 'NEXT'} WAVE IN`,
        waiting: 'THE WAVES START WHEN YOU REACH THE CROSSING',
      });
    }

    const quarters = structure(g, g.kit.shelter, { margin: 1.2 });
    structure(g, 'hut', { margin: 1.2 });
    structure(g, 'alarmPost', { margin: 1 });
    clutter(g, 'crate', 3);
    if (quarters) guardAtDoor(g, quarters);
    guardsAbout(g, 1 + g.rng.int(2));
    return {
      objective: {
        kind: 'holdZone', label: 'HOLD THE CROSSING', targetPropIds: [marker.id], minFraction: 1,
        zone, dwellSeconds: HOLD_SECONDS,
        rules: [
          { type: 'recall', mode: 'disabled' },
          ...rules,
          {
            type: 'timedEvent', atSeconds: 0, effect: 'ospreyArrives', from: 'complete',
            orders: 'HOLD THE FORD FOR 3:00 - THE OSPREY COMES ON ITS OWN WHEN THE HOLD IS DONE',
          },
        ],
      },
      finish(def) {
        // The mission clock must hold a careful man's walk to the ford, the three minutes, and the Osprey.
        const route = measureRoute(def);
        const needed = route ? route.toFirst * CAREFUL + HOLD_SECONDS + OSPREY_SECONDS : 0;
        if (needed > def.clockSeconds) g.map.problems.push('hold-crossing: the clock is too short to reach and hold the ford');
      },
    };
  },

  asserts(def, probe) {
    const { check, out } = checker('hold-crossing');
    const [marker] = checkTargets(check, def, ['observationPost']);
    const o = def.objective;
    check(
      o.kind === 'holdZone' && o.dwellSeconds === HOLD_SECONDS && o.dwellUnseen !== true,
      'three minutes in the zone, paused outside',
    );
    check(ruleOf(def, 'recall')?.mode === 'disabled', 'Recall must be disabled');
    const river = def.waterBarriers[0];
    const zone = o.zone;
    check(river !== undefined && zone !== undefined, 'needs a river and a zone on it');
    if (river && zone && marker) {
      check(insideMap(def, zone), 'the zone must lie inside the map');
      check(
        insideRect(zone, marker.x, marker.z) && inWater(def, marker.x, marker.z),
        'the marker stands in the ford, in the zone',
      );
      check(river.crossings.some((c) => Math.abs(c - marker.x) < 0.01), 'the zone must be on a crossing of the river');
      check(
        !def.props.some((p) => p.kind === 'bridge' && Math.abs(p.x - marker.x) < 3),
        'a ford has no bridge over it',
      );
      // Standing room on both banks, and a dry-shod way to it.
      const bank = (z0: number, z1: number): number => probe.standable({ x0: zone.x0, z0, x1: zone.x1, z1 });
      check(
        bank(zone.z0, zone.z0 + 3) >= 12 && bank(zone.z1 - 3, zone.z1) >= 12,
        'both banks of the ford need standing room',
      );
      check(probe.breach(marker.id) === 0, 'the ford cannot be walked to');
      check(zone.z1 < def.dropBand.z0 && zone.z0 > def.goalZone.z1, 'the ford lies between the drop band and the camp');
    }

    const waves = timedRules(def, 'searchPartyArrives');
    check(
      waves.length === wavesFor(def.threat),
      `needs ${wavesFor(def.threat)} waves at threat ${def.threat}, has ${waves.length}`,
    );
    check(waves.every((w) => w.wakes !== undefined) && new Set(waves.map((w) => w.wakes)).size === waves.length,
      'each wave wakes its own men');
    waves.forEach((wave, i) => {
      const men = waiting(def, wave.wakes ?? '');
      check(
        men.length === waveSizeFor(def.threat),
        `wave ${i + 1} numbers ${waveSizeFor(def.threat)}, not ${men.length}`,
      );
      check(
        men.every((m) => insideRect(def.goalZone, m.x, m.z)),
        `wave ${i + 1} forms up in the camp, out of sight of the ford`,
      );
      const before = waves[i - 1];
      check(
        before === undefined || wave.atSeconds >= before.atSeconds + 30,
        `wave ${i + 1} lands on top of the one before`,
      );
      check(wave.failIfIncomplete !== true, 'a wave is a fight, not a deadline');
      // On the hold's clock, not the mission's: counted from his arrival, and every one inside the three minutes.
      check(wave.from === 'zoneEntered', `wave ${i + 1} must be counted from his arrival at the ford`);
      check(
        wave.atSeconds >= 20 && wave.atSeconds <= HOLD_SECONDS - 30,
        `wave ${i + 1} sets out at ${wave.atSeconds} s: outside the ${HOLD_SECONDS} s hold`,
      );
      check(wave.waiting !== undefined, `wave ${i + 1} must say what it is waiting for`);
    });
    const first = waves[0];
    const prompt = first === undefined || (first.atSeconds >= 20 && first.atSeconds <= 30);
    check(prompt, 'the first wave comes 20-30 s after he arrives');
    const [osprey] = timedRules(def, 'ospreyArrives');
    check(
      osprey !== undefined && osprey.from === 'complete' && osprey.atSeconds === 0,
      'the Osprey comes when the hold is done',
    );
    checkRuleText(check, def);
    return out;
  },
};
